import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Image, Video, Target, CheckCircle2, Clock, AlertTriangle,
  Layout, Trophy, TrendingUp, User, Loader2, BarChart3,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METRIC_CONFIG = {
  POS: { label: 'Poster', icon: Image, color: 'text-pink-400', bg: 'bg-pink-500/10', bar: 'bg-pink-500' },
  VIA: { label: 'Video (Ads)', icon: Video, color: 'text-purple-400', bg: 'bg-purple-500/10', bar: 'bg-purple-500' },
  VID: { label: 'Video (Organic)', icon: Video, color: 'text-fuchsia-400', bg: 'bg-fuchsia-500/10', bar: 'bg-fuchsia-500' },
};
const METRIC_KEYS = ['POS', 'VIA', 'VID'];

export default function CreativeMyScorecardView({ period, targetUserId }) {
  const [tab, setTab] = useState('team'); // 'team' or 'personal'
  const [overview, setOverview] = useState(null);
  const [personal, setPersonal] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('token');
  const h = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    const personalUrl = targetUserId
      ? `${API}/creative-scorecard/my-personal?period=${period}&target_user_id=${targetUserId}`
      : `${API}/creative-scorecard/my-personal?period=${period}`;
    Promise.all([
      axios.get(`${API}/creative-scorecard/team-overview?period=${period}`, h),
      axios.get(personalUrl, h),
    ])
      .then(([ovRes, pRes]) => {
        if (ignore) return;
        setOverview(ovRes.data);
        setPersonal(pRes.data);
      })
      .catch(() => { /* parent handles error UI */ })
      .finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [period, targetUserId, h]);

  if (loading && !overview) {
    return (
      <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-8 text-center" data-testid="creative-view-loading">
        <Loader2 size={20} className="animate-spin mx-auto text-pink-400 mb-2" />
        <p className="text-sm text-[hsl(var(--text-muted))]">Loading creative scorecard...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="creative-my-scorecard-view">
      {/* Tab switcher */}
      <div className="flex items-center gap-2 bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-lg p-1 w-fit">
        <button
          data-testid="creative-tab-team"
          onClick={() => setTab('team')}
          className={`px-4 py-1.5 rounded-md text-xs font-semibold transition flex items-center gap-1.5 ${
            tab === 'team'
              ? 'bg-gradient-to-r from-pink-500 to-rose-600 text-white shadow-[0_2px_8px_rgba(236,72,153,0.3)]'
              : 'text-[hsl(var(--text-muted))] hover:text-white'
          }`}
        >
          <Layout size={12} /> Team Creative
        </button>
        <button
          data-testid="creative-tab-personal"
          onClick={() => setTab('personal')}
          className={`px-4 py-1.5 rounded-md text-xs font-semibold transition flex items-center gap-1.5 ${
            tab === 'personal'
              ? 'bg-gradient-to-r from-pink-500 to-rose-600 text-white shadow-[0_2px_8px_rgba(236,72,153,0.3)]'
              : 'text-[hsl(var(--text-muted))] hover:text-white'
          }`}
        >
          <User size={12} /> My Personal
        </button>
      </div>

      {tab === 'team' && <TeamCreativeTab overview={overview} />}
      {tab === 'personal' && <PersonalCreativeTab personal={personal} />}
    </div>
  );
}

const TeamCreativeTab = ({ overview }) => {
  if (!overview) return null;
  const team = overview.team || {};
  const channels = overview.channels || [];
  const aggregateOk = (team.total_target || 0) > 0 || (team.total_actual || 0) > 0;
  return (
    <div className="space-y-4" data-testid="creative-team-tab">
      {/* Team aggregate header */}
      <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-base font-semibold flex items-center gap-2"><Target size={16} className="text-pink-400" /> Team Creative — All Channels Aggregate</h3>
            <p className="text-xs text-[hsl(var(--text-muted))] mt-0.5">Total targets summed from per-channel "Creative" scorecard nodes.</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wide text-[hsl(var(--text-muted))]">Total Done</div>
              <div className="text-2xl font-bold text-emerald-400" data-testid="creative-team-actual">{team.total_actual || 0}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wide text-[hsl(var(--text-muted))]">Target</div>
              <div className="text-2xl font-bold text-[hsl(var(--text-secondary))]" data-testid="creative-team-target">{team.total_target || 0}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wide text-[hsl(var(--text-muted))]">Completion</div>
              <div className="text-2xl font-bold text-amber-400" data-testid="creative-team-completion">{team.completion_pct || 0}%</div>
            </div>
          </div>
        </div>
        <Progress value={team.completion_pct || 0} className="h-2" />
        <div className="grid grid-cols-3 gap-3 mt-4">
          {METRIC_KEYS.map((mk) => {
            const cfg = METRIC_CONFIG[mk];
            const Icon = cfg.icon;
            const tgt = team.targets?.[mk] || 0;
            const actual = team.actuals?.[mk] || 0;
            const pct = tgt > 0 ? Math.min(100, Math.round((actual / tgt) * 100)) : 0;
            return (
              <div key={mk} className="border border-[hsl(var(--border))] rounded-lg p-3" data-testid={`creative-team-metric-${mk}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`p-1.5 rounded ${cfg.bg}`}><Icon size={14} className={cfg.color} /></span>
                  <span className="text-xs font-semibold">{cfg.label}</span>
                </div>
                <div className="flex items-baseline gap-1">
                  <span className={`text-xl font-bold ${cfg.color}`}>{actual}</span>
                  <span className="text-xs text-[hsl(var(--text-muted))]">/{tgt}</span>
                </div>
                <div className="w-full bg-[hsl(var(--surface-elevated))] rounded-full h-1 overflow-hidden mt-1">
                  <div className={`${cfg.bar} h-full transition-all`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Per-channel breakdown */}
      <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-5">
        <h3 className="text-sm font-semibold flex items-center gap-2 mb-3"><Layout size={14} className="text-cyan-400" /> Per-Channel Scorecard</h3>
        {channels.length === 0 ? (
          <p className="text-sm text-[hsl(var(--text-muted))] py-4 text-center">No Creative org nodes configured.</p>
        ) : !aggregateOk ? (
          <div className="text-sm text-amber-400/80 py-3 px-3 bg-amber-500/5 border border-amber-500/20 rounded-lg">
            ⚠ No targets set for any Creative node in this period. Admin → Scorecard → Targets → pick each "Creative" node and set POS, VIA, VID.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {channels.map((c) => {
              const totalTgt = c.total_target || 0;
              const pct = totalTgt > 0 ? Math.min(100, Math.round((c.total_actual / totalTgt) * 100)) : 0;
              const remaining = Math.max(0, totalTgt - (c.total_actual || 0));
              return (
                <div key={c.node_id} className="border border-[hsl(var(--border))] rounded-lg p-3 bg-[hsl(var(--surface-elevated))]/40" data-testid={`creative-channel-card-${c.node_id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-semibold text-sm truncate">{c.channel_name}</div>
                    {c.is_saas && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded">SaaS</span>}
                  </div>
                  <div className="space-y-1.5 text-xs">
                    {METRIC_KEYS.map((mk) => {
                      const cfg = METRIC_CONFIG[mk];
                      const tgt = c.targets?.[mk] || 0;
                      const act = c.actuals?.[mk] || 0;
                      const remain = Math.max(0, tgt - act);
                      const mpct = tgt > 0 ? Math.min(100, Math.round((act / tgt) * 100)) : 0;
                      return (
                        <div key={mk}>
                          <div className="flex items-center justify-between gap-1 mb-0.5">
                            <span className={`text-[10px] font-bold ${cfg.color}`}>{cfg.label}</span>
                            <span className="text-[10px] tabular-nums">
                              <span className={cfg.color + ' font-bold'}>{act}</span>
                              <span className="text-[hsl(var(--text-muted))]">/{tgt}</span>
                              {remain > 0 && <span className="text-amber-400 ml-1">· {remain} left</span>}
                            </span>
                          </div>
                          <div className="flex-1 bg-[hsl(var(--surface))] rounded-full h-1 overflow-hidden">
                            <div className={`${cfg.bar} h-full`} style={{ width: `${mpct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="border-t border-[hsl(var(--border))] mt-2 pt-1.5 flex items-center justify-between text-[11px]">
                    <span className="text-[hsl(var(--text-muted))]">Channel Total</span>
                    <span className={`font-bold ${pct >= 80 ? 'text-emerald-400' : pct >= 50 ? 'text-amber-400' : 'text-red-400'}`}>{c.total_actual}/{totalTgt} · {pct}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const PersonalCreativeTab = ({ personal }) => {
  if (!personal) return null;
  const s = personal.summary || {};
  const im = personal.individual_targets || {};
  const pm = personal.per_metric || {};
  const tm = personal.templates || {};
  return (
    <div className="space-y-4" data-testid="creative-personal-tab">
      {/* Top summary */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <SmallStat label="Total Orders" value={s.total_orders || 0} accent="text-amber-400" icon={Trophy} testid="cp-personal-orders" />
        <SmallStat label="Completed" value={s.completed || 0} accent="text-emerald-400" icon={CheckCircle2} testid="cp-personal-completed" />
        <SmallStat label="In Progress" value={s.in_progress || 0} accent="text-blue-400" icon={Clock} testid="cp-personal-inprogress" />
        <SmallStat label="Overdue" value={s.overdue || 0} accent="text-red-400" icon={AlertTriangle} testid="cp-personal-overdue" />
        <SmallStat label="On-Time" value={`${s.on_time_pct || 0}%`} accent="text-amber-400" icon={Target} testid="cp-personal-ontime" />
      </div>

      {/* Individual target vs personal output */}
      <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-5" data-testid="cp-personal-individual-target">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-base font-semibold flex items-center gap-2"><User size={16} className="text-pink-400" /> My Individual Target</h3>
            <p className="text-xs text-[hsl(var(--text-muted))] mt-0.5">
              Auto-split rata dari team target ÷ {personal.team_size || 0} ahli creative
            </p>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wide text-[hsl(var(--text-muted))]">Contribution to Team</div>
            <div className="text-2xl font-bold text-amber-400" data-testid="cp-personal-contribution">{s.contribution_pct || 0}%</div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {METRIC_KEYS.map((mk) => {
            const cfg = METRIC_CONFIG[mk];
            const Icon = cfg.icon;
            const tgt = im[mk] || 0;
            const actual = pm[mk]?.actual || 0;
            const pct = tgt > 0 ? Math.min(100, Math.round((actual / tgt) * 100)) : 0;
            const remain = Math.max(0, tgt - actual);
            return (
              <div key={mk} className="border border-[hsl(var(--border))] rounded-lg p-3" data-testid={`cp-personal-metric-${mk}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`p-1.5 rounded ${cfg.bg}`}><Icon size={14} className={cfg.color} /></span>
                    <span className="text-sm font-semibold">{cfg.label}</span>
                  </div>
                  <span className="text-[10px] text-[hsl(var(--text-muted))]">Target: <span className="font-bold text-[hsl(var(--text-primary))]">{tgt}</span></span>
                </div>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className={`text-2xl font-bold ${cfg.color}`}>{actual}</span>
                  <span className="text-xs text-[hsl(var(--text-muted))]">/ {tgt} done</span>
                  {remain > 0 && <span className="text-xs text-amber-400 ml-auto">{remain} left</span>}
                </div>
                <div className="w-full bg-[hsl(var(--surface-elevated))] rounded-full h-1.5 overflow-hidden">
                  <div className={`${cfg.bar} h-full transition-all`} style={{ width: `${pct}%` }} />
                </div>
                <div className="flex items-center justify-between text-[10px] mt-1.5">
                  <span className="text-[hsl(var(--text-muted))]">{pm[mk]?.total_ordered || 0} orders</span>
                  <span className={`font-semibold ${pct >= 80 ? 'text-emerald-400' : pct >= 50 ? 'text-amber-400' : 'text-red-400'}`}>{pct}%</span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="border-t border-[hsl(var(--border))] mt-4 pt-3 flex items-center justify-between text-xs">
          <div className="flex items-center gap-2 text-[hsl(var(--text-muted))]">
            <BarChart3 size={12} className="text-[hsl(var(--text-muted))]" />
            <span>Avg Turnaround: <span className="text-[hsl(var(--text-primary))] font-semibold">{s.avg_turnaround_days != null ? `${s.avg_turnaround_days} days` : '—'}</span></span>
          </div>
          {!personal.is_team_member && (
            <Badge className="bg-amber-500/10 text-amber-400 border border-amber-500/20 text-[10px]">
              You're not in the configured creative team list — admin needs to add you.
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
};

const SmallStat = ({ label, value, accent, icon: Icon, testid }) => (
  <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-3 flex items-center gap-3" data-testid={testid}>
    <div className="p-2 rounded-lg bg-[hsl(var(--surface-elevated))]"><Icon size={18} className={accent} /></div>
    <div>
      <div className="text-[10px] uppercase tracking-wide text-[hsl(var(--text-muted))]">{label}</div>
      <div className={`text-xl font-bold ${accent}`}>{value}</div>
    </div>
  </div>
);
